import React, {useEffect, useState} from 'react';
import {
    ButtonGroup,
    Dialog,
    DialogContent,
    Menu,
    MenuItem,
    Tab,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography
} from '@mui/material';
import {createTheme, styled} from '@mui/material/styles';
import ProductService from '../../services/product.service';
import Table from '@mui/material/Table';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import Button from "@mui/material/Button";
import VOCSETLayout from '../../views/VOCSETLayout';
import {dropdownItems} from '../../views/dropdownItems';
import {MoonLoader} from "react-spinners";
import {APIResponse, APIResult, Product, Exchange} from "../../types/APITypes";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import * as XLSX from 'xlsx';
import {FileUpload} from "@mui/icons-material";
import ExchangeService from "../../services/exchange.service";

createTheme();

const ProductDashboard = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [mics, setMics] = useState<string[]>([]);
    const [error, setError] = useState<string>('');
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(true);
    const [selectedMic, setSelectedMic] = useState<string>('');
    const [privileges, setPrivileges] = useState<string[]>([]);
    const [action, setAction] = useState<'upload' | 'activate' | 'delete' | 'error' | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [uploadSummary, setUploadSummary] = useState<{ ok: number; error: string[] }>({
        ok: 0,
        error: []
    });
    const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [contextMic, setContextMic] = useState<string>('');
    const navigate = useNavigate();
    const location = useLocation();

    const resize = () => {
        setOpen(window.innerWidth >= 760);
    };

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    useEffect(() => {
        const storedPrivileges = localStorage.getObj('privileges');
        if (storedPrivileges) {
            setPrivileges(storedPrivileges);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('resize', resize);
        resize();
        return () => {
            window.removeEventListener('resize', resize);
        };
    }, []);

    useEffect(() => {
        const fetchData= async() => {
            try {
                const fetchedExchanges = await ExchangeService.getExchanges(false);
                if (fetchedExchanges && fetchedExchanges.length > 0) {
                    const allExchangeCodes = fetchedExchanges.map(e => e.mic );
                    setMics(allExchangeCodes);
                    const fetchedProducts = await ProductService.getProducts(true);
                    if (fetchedProducts && fetchedProducts.length > 0) {
                        setProducts(fetchedProducts);
                        const passedMic = location.state?.selectedMic;
                        const micToSet = passedMic && allExchangeCodes.includes(passedMic)
                            ? passedMic
                            : allExchangeCodes[0];
                        setSelectedMic(micToSet);
                        setLoading(false);

                    } else {
                        setError('No products available.');
                        setLoading(false);

                    }
                }
            } catch (error: any) {
                console.error('Error fetching products:', error.response ? error.response.data : error);
                setError(error?.response?.data?.result?.[0]?.message || 'Error fetching products. Please try again.');
            }
        };

        fetchData();
    }, [location.state]);

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);

    const handleActivationClick = (event: React.MouseEvent, product: Product) => {
        event.stopPropagation();
        handleActivation(product);
    };

    const handleDeleteClick = (event: React.MouseEvent, uuid: string) => {
        event.stopPropagation();
        handleDelete(uuid);
    };

    const handleActivation = async (product: Product) => {
        try {
            await ProductService.updateProduct({...product, active: true});
            setAction('activate');
        } catch (error: any) {
            console.error('Error activating product:', error);
            setError(error?.response?.data?.result?.[0]?.message || 'Error activating product. Please try again.');
            setAction('error');
        } finally {
            handleOpenDialog();
            setTimeout(handleCloseDialog, 2000);
        }
    };

    const handleDelete = async (uuid: string) => {
        try {
            await ProductService.deleteProduct(uuid);
            setAction('delete');
        } catch (error: any) {
            setError(error?.response?.data?.result?.[0]?.message || 'Error deleting product. Please try again.');
            setAction('error');
        } finally {
            handleOpenDialog();
            setTimeout(handleCloseDialog, 2000);
        }
    };

    const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = async (e) => {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, {type: 'array'});
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const parsedData = XLSX.utils.sheet_to_json(worksheet, {defval: ""}) as any[];

            const jsonData = parsedData.map(row => ({
                assetClass: row.asset_class?.trim() || undefined,
                contractCode: row.contract_code || undefined,
                gmiCode: row.gmi_code || undefined,
                currency: row.currency|| undefined,
                underlier: row.underlier || undefined,
                multiplier: row.multiplier || undefined,
                contractSize: row.contractSize || undefined,
                uom: row.contract_uom || undefined,
                settlementType: row.settlement_type?.trim() || "Cash",
                maturityRule: row.maturity_rule || undefined,
                url: row.url || undefined,
                mic: selectedMic,
                shortDescription: row.short_description?.trim() || undefined,
                description: row.description?.trim() || undefined
            }));

            try {
                const response: APIResponse = await ProductService.createProduct(jsonData, selectedMic);
                const result: APIResult[] = response.result as unknown as APIResult[];

                setUploadSummary({
                    ok: result.filter(r => r.status === "OK").length,
                    error: result
                        .filter(r => r.status === "ERROR")
                        .map(r => r.id || 'Unknown error')
                });
                setAction('upload');
            } catch (error) {
                console.error("Upload failed:", error);
                setError('Failed to upload file.');
                setAction('error');
            } finally {
                handleOpenDialog();
                setTimeout(handleCloseDialog, 2000);
            }
        };

        reader.readAsArrayBuffer(file);
    };

    let dialogContent = '';
    if (action === 'activate') dialogContent = 'Activation complete';
    else if (action === 'delete') dialogContent = 'Deactivation complete';
    else if (action === 'upload') {
        const {ok, error} = uploadSummary;
        dialogContent = `Upload Summary: ${ok} successful`;
        if (error.length > 0) {
            dialogContent += `, ${error.length} failed`;
        }
    } else if (action === 'error') dialogContent = error;

    if (loading || !selectedMic) {
        return <MoonLoader color="#282c34" />;
    }

    return (
        <div>
            <VOCSETLayout dropdownItems={dropdownItems}>
                {privileges.includes('CREATE_ANY_PRODUCT') && (
                    <ButtonGroup style={{gap: '10px'}}>
                        <Button
                            aria-label="New Product"
                            variant="contained"
                            component={Link}
                            to="/CreateProduct"
                            startIcon={<AddIcon/>}
                        >
                            New Product
                        </Button>
                    </ButtonGroup>
                )}
                <Tabs
                    value={selectedMic}
                    onChange={(e, val) => setSelectedMic(val)}
                    aria-label="Mic Tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {mics.map((mic, idx) => (
                        <Tab
                            key={idx}
                            label={mic}
                            value={mic}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                setSelectedMic(mic);
                                setContextMenuAnchorEl(e.currentTarget);
                                setContextMic(mic);
                            }}
                        />
                    ))}
                </Tabs>
                <VisuallyHiddenInput
                    id="hidden-file-input"
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFile}
                />
                {loading ? (
                    <MoonLoader color="#282c34"/>
                ) : products.length === 0 ? (
                    <p>No products found.</p>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Contract Code</TableCell>
                                    <TableCell>Asset Class</TableCell>
                                    <TableCell>GMI Code</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Currency</TableCell>
                                    {(privileges.includes('WRITE_ANY_PRODUCT')) && (
                                        <>
                                            <TableCell>Underlier</TableCell>
                                            <TableCell>Multiplier</TableCell>
                                            <TableCell>Contract Size</TableCell>
                                            <TableCell>UOM</TableCell>
                                            <TableCell>Settlement Type</TableCell>
                                        </>
                                    )}
                                    {privileges.includes('WRITE_ANY_PRODUCT') && <TableCell>Actions</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {products.filter(p => p.mic === selectedMic).map(product => (
                                    <TableRow key={product.id}
                                              style={{backgroundColor: !product.active ? '#f5f5f5' : '' }}>
                                        <TableCell>{product.contractCode}</TableCell>
                                        <TableCell>
                                            {product.url ? (
                                                <a
                                                    href={product.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: '#1976d2',
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {product.assetClass}
                                                </a>
                                            ) : (
                                                product.assetClass
                                            )}
                                        </TableCell>
                                        <TableCell>{product.gmiCode}</TableCell>
                                        <TableCell>{product.shortDescription}</TableCell>
                                        <TableCell>{product.currency}</TableCell>
                                        {(privileges.includes('WRITE_ANY_PRODUCT')) && (
                                            <>
                                                <TableCell>{product.underlier}</TableCell>
                                                <TableCell>{product.multiplier}</TableCell>
                                                <TableCell>{product.contractSize}</TableCell>
                                                <TableCell>{product.uom}</TableCell>
                                                <TableCell>{product.settlementType}</TableCell>
                                            </>
                                        )}
                                        {privileges.includes('WRITE_ANY_PRODUCT') && (
                                            <TableCell>
                                                <ButtonGroup className="dash-button-group">
                                                    <Link
                                                        to={`/EditProduct`}
                                                        state={product}
                                                        style={{textDecoration: 'none', color: 'inherit'}}
                                                    >
                                                        <Button className="dash-button" startIcon={<EditIcon/>}/>
                                                    </Link>
                                                    {!product.active ? (
                                                        <Button
                                                            className="dash-button"
                                                            startIcon={<DoneIcon />}
                                                            onClick={e => handleActivationClick(e, product)}
                                                        />
                                                    ) : (
                                                        <Button
                                                            className="dash-button"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={e => handleDeleteClick(e, product.id)}
                                                        />
                                                    )}
                                                </ButtonGroup>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </VOCSETLayout>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogContent>
                    <Typography variant="body1">{dialogContent}</Typography>
                </DialogContent>
            </Dialog>

            {privileges.includes('CREATE_ANY_PRODUCT') && (
            <Menu
                open={Boolean(contextMenuAnchorEl)}
                anchorEl={contextMenuAnchorEl}
                onClose={() => setContextMenuAnchorEl(null)}
            >
                <MenuItem
                    onClick={() => {
                        setSelectedMic(contextMic);
                        setTimeout(() => {
                            document.getElementById('hidden-file-input')?.click();
                        }, 0);
                        setContextMenuAnchorEl(null);
                    }}
                >
                    <FileUpload style={{marginRight: '8px'}}/>
                    Upload File
                </MenuItem>
            </Menu>
            )}

        </div>
    );
};

export default ProductDashboard;
